/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import PaginationCounter from "../../../components/Pagination/PaginationCounter";
import Tabs, { TabPanel } from "../../../components/Tabs";
import TitleBox from "../../../components/TitleBox";
import { selectContactInfo, selectCurrentFarmerAccount } from "../../../features/azure-b2c/storage";
import ProductFilter, {
  ComponentFilters,
} from "../../../features/products/components/ProductFilter";
import { useProductFilters } from "../../../features/products/hooks/useProductFilters";
import { AllProductsCover } from "../../../features/products/pages/AllProductsCover";
import PastProductsTab from "../../../features/products/pages/PastProductsTab";
import {
  Product,
  usePastProducts,
  usePastProductsWithActivePrices,
} from "../../../features/products/storage";
import { filterProducts } from "../../../features/products/utils/filterProducts";
import useQuoteProvider from "../../../features/quotes/components/QuoteModal/useQuoteModal";
import { getCategories } from "../../../features/typesense/api";
import TypesenseSearch from "../../../features/typesense/Search";
import { usePermission } from "../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { TestIds } from "../../../testIds";
import { PAGINATION_DATA_LIMIT, SortingOptions } from "../../../util/consts";
import { useBranch } from "../../../util/useBranch";
import { useScrollRestoration } from "../../../util/useScrollRestoration";
import { CategoryResponse } from "../../typesense/types";
import { PRODUCTS_ROUTES, ProductTabIndex } from "../constants";

const AllProductsFilters = [
  ComponentFilters.SEARCH_TYPESENSE,
  ComponentFilters.CATEGORY,
  ComponentFilters.PRICE,
  ComponentFilters.IN_STOCK,
];

const AllProductsFiltersWithPromotion = [
  ComponentFilters.SEARCH_TYPESENSE,
  ComponentFilters.CATEGORY,
  ComponentFilters.PROMOTION,
  ComponentFilters.PRICE,
  ComponentFilters.IN_STOCK,
];

const PastProductsFilters = [
  ComponentFilters.SEARCH_PAST,
  ComponentFilters.CATEGORY,
  ComponentFilters.DATE,
  ComponentFilters.PRICE,
];

type ProductTab = {
  label: string;
  "data-testid": string;
  filterComponents: ComponentFilters[];
  details: React.ReactNode;
  default: boolean;
};

export const ProductsPage = () => {
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const { traderDealsEnabled } = useSelector(selectRemoteConfig);
  const { data = [], isPending: isPastProductsPending } = usePastProducts(
    selectedAccount?.accountNumber
  );

  const [categories, setCategories] = useState<string[]>([]);

  const { allowed: canViewPreviousOrders, isLoading: isPreviousOrdersPermissionLoading } =
    usePermission("FarmlandsProShop_PreviousOrdersView");

  const relationshipOwner = useSelector(selectContactInfo);

  const {
    selectedFilters,
    sortBy,
    setSortBy,
    tab,
    setTab,
    currentPage,
    clearFilters,
    getIsFilterApplied,
  } = useProductFilters();

  const filtersByTab: Record<ProductTabIndex, ComponentFilters[]> = {
    [ProductTabIndex.All]: traderDealsEnabled
      ? AllProductsFiltersWithPromotion
      : AllProductsFilters,
    [ProductTabIndex.Past]: PastProductsFilters,
  };

  const isFilterApplied = getIsFilterApplied(
    filtersByTab[tab as ProductTabIndex] ?? filtersByTab[ProductTabIndex.All]
  );

  const [showCover, setShowCover] = useState(!isFilterApplied);

  useEffect(() => {
    (async () => {
      const { categories: facetCategories } = await getCategories();
      setCategories(facetCategories.map((category: CategoryResponse) => category.value));
    })();
  }, []);

  useEffect(() => {
    if (isFilterApplied) {
      setShowCover(false);
      document
        .getElementById(TestIds.pages.mainScrollContainer)
        ?.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [isFilterApplied]);

  const { pathname } = useLocation();

  const showPastProductsTab =
    canViewPreviousOrders || (isPreviousOrdersPermissionLoading && tab === ProductTabIndex.Past);

  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnProducts,
    path: pathname,
  });

  useEffect(() => {
    const element = document.getElementById(TestIds.pages.mainScrollContainer);
    if (element) {
      element.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [currentPage, selectedAccount]);

  // Apply branch-specific active prices to the whole list before filtering and
  // sorting so price sort and the price-range filter operate on the same prices
  // shown on the product cards. Only priced while the past tab is active.
  const isPastTab = showPastProductsTab && tab === ProductTabIndex.Past;
  const pricingBranchId = useBranch();
  const { data: pricedPastProducts } = usePastProductsWithActivePrices(
    selectedAccount?.accountNumber,
    isPastTab ? data : [],
    pricingBranchId
  );
  const pastProductsForFiltering = pricedPastProducts ?? data;

  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  useEffect(() => {
    const freshFilteredProducts = filterProducts(
      pastProductsForFiltering,
      {
        category: selectedFilters.category.value,
        searchText: selectedFilters.searchText.value,
        dateRange: selectedFilters.dateRange.value,
        priceRange: selectedFilters.priceRange.value,
      },
      sortBy as SortingOptions
    );

    setFilteredProducts(freshFilteredProducts);
  }, [
    pastProductsForFiltering,
    sortBy,
    selectedFilters.category.value,
    selectedFilters.searchText.value,
    selectedFilters.dateRange.value,
    selectedFilters.priceRange.value,
    selectedFilters.inStock.value,
  ]);

  const paginatedData = React.useMemo(() => {
    const startIndex = currentPage * PAGINATION_DATA_LIMIT - PAGINATION_DATA_LIMIT;
    const endIndex = startIndex + PAGINATION_DATA_LIMIT;
    const products = [...filteredProducts].slice(startIndex, endIndex);
    return products;
  }, [filteredProducts, currentPage]);

  const noPagination = filteredProducts.length <= PAGINATION_DATA_LIMIT;
  const paginationCounter = useMemo(() => {
    return (
      <PaginationCounter
        data-testid={TestIds.components.pagination.number}
        label="products"
        itemsPerPage={PAGINATION_DATA_LIMIT}
        count={paginatedData.length}
        total={filteredProducts.length}
        currentPage={currentPage}
      />
    );
  }, [paginatedData, filteredProducts.length, currentPage]);

  const tabs: ProductTab[] = [
    {
      label: "All products",
      "data-testid": TestIds.feat.product.tabView.allProductsTab,
      filterComponents: filtersByTab[ProductTabIndex.All],
      details: (
        <>
          {showCover && <AllProductsCover selectedFilters={selectedFilters} />}
          {!showCover && (
            <TypesenseSearch
              accountNumber={selectedAccount?.accountNumber}
              setCategoriesCallback={setCategories}
              data={data}
              handleClearFilters={clearFilters}
              isFilterApplied={isFilterApplied}
              sortBy={sortBy}
              setSortBy={setSortBy}
              selectedFilters={selectedFilters}
              currentPage={currentPage}
              relationshipOwner={relationshipOwner}
              path={PRODUCTS_ROUTES.BASE}
            />
          )}
        </>
      ),
      default: tab === ProductTabIndex.All,
    },
    ...(showPastProductsTab
      ? [
          {
            label: "Previously ordered",
            "data-testid": TestIds.feat.product.tabView.pastProductsTab,
            filterComponents: filtersByTab[ProductTabIndex.Past],
            details: (
              <PastProductsTab
                setCategoriesCallback={setCategories}
                paginationCounter={paginationCounter}
                data={data}
                filteredProducts={filteredProducts}
                handleClearFilters={clearFilters}
                isFilterApplied={isFilterApplied}
                isPastProductsPending={isPastProductsPending}
                sortBy={sortBy}
                setSortBy={setSortBy}
                paginatedData={paginatedData}
                noPagination={noPagination}
                currentPage={currentPage}
                relationshipOwner={relationshipOwner}
              />
            ),
            default: tab === ProductTabIndex.Past,
          },
        ]
      : []),
  ];

  // Permission can hide a tab between renders (e.g. URL deep-links to ?tab=1
  // for a user without that permission, or permission revoked mid-session).
  const selectedTabIsHidden = !isPreviousOrdersPermissionLoading && tab >= tabs.length;
  useEffect(() => {
    if (selectedTabIsHidden) {
      setTab(ProductTabIndex.All);
    }
  }, [selectedTabIsHidden]);

  const currentTab = tabs[tab] ?? tabs[0];

  return (
    <PageContainer
      data-testid={TestIds.pages.pastProducts}
      sx={{
        gap: "24px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TitleBox display="Shop" />
      <Tabs
        value={tab}
        setValue={setTab}
        tabsInfo={tabs}
        aria-label="Product search tabs"
        id="product-tabs"
      />
      <ProductFilter
        components={currentTab.filterComponents}
        filters={{ categories }}
        selectedFilters={selectedFilters}
        items={data}
      />
      {tabs.map((x, index) => (
        <TabPanel key={`${x.label}-${tab}`} value={tab} index={index} id={"product-panels"}>
          {x.details}
        </TabPanel>
      ))}
    </PageContainer>
  );
};

export default () => useQuoteProvider(ProductsPage);
